import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { NotificationCard } from '../notifications/components/NotificationCard';
import {
  useRecentNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from '../notifications/hooks/useNotifications';

export interface NotificationDropdownProps {
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onClose,
}) => {
  const navigate = useNavigate();
  const { data, isLoading } = useRecentNotifications();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteNotification = useDeleteNotification();

  const notifications = data?.notifications ?? [];
  const hasUnread = notifications.some((n) => !n.read);

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id);
  };

  const handleDismiss = (id: string) => {
    deleteNotification.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleViewAll = () => {
    onClose();
    navigate('/dashboard/notifications');
  };

  return (
    <div
      className="absolute right-0 top-full mt-2 w-[400px] max-h-[520px] bg-white dark:bg-dark-900 border border-dark-200 dark:border-dark-700 rounded-xl shadow-xl z-dropdown flex flex-col overflow-hidden"
      role="dialog"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-200 dark:border-dark-700">
        <h3 className="text-sm font-semibold text-dark-900 dark:text-dark-100">
          Notifications
        </h3>
        {hasUnread && (
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            disabled={markAllAsRead.isPending}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 transition-colors"
          >
            {markAllAsRead.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <CheckCheck className="w-3.5 h-3.5" />
            )}
            Mark all as read
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="w-12 h-12 rounded-full bg-dark-100 dark:bg-dark-800 flex items-center justify-center mb-3">
              <Bell className="w-5 h-5 text-dark-400" />
            </div>
            <p className="text-sm font-medium text-dark-900 dark:text-dark-100">
              No notifications
            </p>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-1 text-center">
              You're all caught up! Notifications will appear here.
            </p>
          </div>
        ) : (
          <div className="p-2 flex flex-col gap-1">
            {notifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onDismiss={handleDismiss}
                compact
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-dark-200 dark:border-dark-700 p-2">
          <button
            type="button"
            onClick={handleViewAll}
            className="w-full py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-500/5 rounded-lg transition-colors"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

NotificationDropdown.displayName = 'NotificationDropdown';